"use client";

import Button from "@/components/Button";
import { Treatment } from "@/constants/treatments";
import TREATMENTS from "@/constants/treatments";
import { PillBGMapping, PillMapping } from "@/utils/discover-mapping";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Route } from "next";
import Link from "next/link";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useState } from "react";

const Pricing = ({
    dropdownBg,
    howItWorksAvailable = true,
}: {
    dropdownBg?: string;
    howItWorksAvailable?: boolean;
}) => {
    const router = useRouter();
    const [selectedTreatment, setSelectedTreatment] =
        useState<Treatment | null>(TREATMENTS[0]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const PriceBox = ({
        title,
        description,
        price,
    }: {
        title: string;
        description: string[];
        price: string;
    }) => (
        <div className="w-full md:w-[466px] relative">
            <div className="text-center text-[#333333] dark:text-white text-2xl md:text-[28px] font-medium font-satoshi leading-[30.80px]">
                {title}
            </div>
            <div className="flex flex-col items-center gap-1 mt-4 md:mt-5">
                <div className="text-center text-[#1a5b77] dark:text-blue-400 text-sm md:text-base font-light font-space leading-tight">
                    {description.map((line, index) => (
                        <span key={index}>
                            {line}
                            <br />
                        </span>
                    ))}
                </div>
                <div className="text-center text-[#1a5b77] dark:text-blue-400 text-4xl md:text-[50px] font-light font-space leading-[60px]">
                    {price}
                </div>
            </div>
        </div>
    );

    const TreatmentSelector = () => (
        <div className="relative w-full lg:min-w-1/3">
            <div
                className={`w-full border-b border-[#1a5b77] dark:border-blue-500 cursor-pointer bg-card rounded-2xl`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                <div className="w-full relative flex items-center justify-between py-2 px-2">
                    <div className="flex items-center">
                        <div
                            className={`${selectedTreatment ? PillBGMapping[selectedTreatment.id] : "bg-[#cbd9ee]"} rounded-md`}
                        >
                            {selectedTreatment
                                ? PillMapping[selectedTreatment.id]?.(60)
                                : null}
                        </div>
                        <span className="ml-[17px] text-[#1a5b77] dark:text-blue-400 text-xl lg:text-[28px] font-medium font-satoshi uppercase truncate">
                            {selectedTreatment?.name}
                        </span>
                    </div>
                    <ChevronDownIcon
                        className={`w-6 h-6 text-[#1a5b77] dark:text-blue-400 transition-transform duration-200 shrink-0 ${isDropdownOpen ? "rotate-180" : ""}`}
                    />
                </div>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="absolute top-full left-0 w-full bg-popover shadow-lg rounded-b-lg z-10 mt-1 divide-y-2 dark:divide-gray-600 rounded-2xl">
                    {TREATMENTS.filter(
                        (treatment) =>
                            treatment.pricing && treatment.pricing.pharmacyCost,
                    ).map((treatment) => (
                        <div
                            key={treatment.id}
                            className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                            onClick={() => {
                                posthog?.capture("treatment_selected", {
                                    treatmentName: treatment.name,
                                });
                                setSelectedTreatment(treatment);
                                setIsDropdownOpen(false);
                            }}
                        >
                            <div
                                className={`${PillBGMapping[treatment.id] ?? "bg-[#cbd9ee]"} rounded-md`}
                            >
                                {PillMapping[treatment.id]?.(48)}
                            </div>
                            <span className="ml-4 text-[#1a5b77] dark:text-blue-400 text-xl font-medium font-satoshi uppercase">
                                {treatment.name}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const secondDescription = selectedTreatment?.pricing.prescriptionLength
        ? [
              `Estimated ${selectedTreatment.pricing.prescriptionLength} day supply price`,
              "you pay at pharmacy",
          ]
        : ["Estimated 90 day supply price", "you pay at pharmacy"];

    const thirdDescription = selectedTreatment?.carecard
        ? ["With the CareCard", "discount"]
        : ["With the RTHM", "discount"];

    const thirdTitle = selectedTreatment?.pricing.prescriptionLength
        ? `Estimated Total for ${selectedTreatment.pricing.prescriptionLength} days`
        : `Estimated Total for 90 days`;

    return (
        <div className="py-8 flex flex-col items-center gap-4">
            <h1 className="text-[#333333] dark:text-white text-[44px] font-semibold font-satoshi uppercase leading-[57.20px] text-center max-w-[808px]">
                Pricing
            </h1>

            <div className="w-full flex flex-col items-center gap-4">
                {/* Treatment Selection */}
                <div className="flex flex-col items-center gap-6 w-full max-w-xl">
                    <div className="text-[#333333] dark:text-gray-300 text-xl font-normal font-space leading-7 text-center">
                        Select a Treatment
                    </div>

                    <TreatmentSelector />
                </div>

                {/* Pricing Details */}
                {selectedTreatment?.pricing && (
                    <div className="w-full bg-card rounded-2xl p-4 md:p-8">
                        <div className="flex flex-col md:flex-row md:justify-between">
                            <PriceBox
                                title="RTHM"
                                description={[
                                    "Online assessment fee for prescription",
                                    "Only pay if prescription is approved",
                                ]}
                                price={
                                    selectedTreatment.pricing.consultationPrice
                                }
                            />

                            {/* Divider that only shows on desktop */}
                            <div className="hidden md:block w-px h-[137px] bg-[#333333]/20 dark:bg-blue-400" />

                            {/* Mobile divider */}
                            <div className="md:hidden w-full h-px bg-[#333333]/20 my-4 dark:bg-blue-400" />

                            <PriceBox
                                title="Pharmacy Cost"
                                description={secondDescription}
                                price={selectedTreatment.pricing.pharmacyCost}
                            />

                            {/* Divider that only shows on desktop */}
                            <div className="hidden md:block w-px h-[137px] bg-[#333333]/20 dark:bg-blue-400" />

                            {/* Mobile divider */}
                            <div className="md:hidden w-full h-px bg-[#333333]/20 my-4 dark:bg-blue-400" />

                            <PriceBox
                                title={thirdTitle}
                                description={thirdDescription}
                                price={selectedTreatment.pricing.estimate}
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col md:flex-row justify-center gap-2 mt-8">
                            <Button
                                text="Explore this treatment"
                                id={
                                    "explore-treatment" + selectedTreatment.name
                                }
                                type="filled"
                                onClick={() => {
                                    posthog?.capture(
                                        "explore_treatment_button_clicked",
                                        {
                                            treatmentName:
                                                selectedTreatment.name,
                                        },
                                    );
                                    const formattedTreatmentName =
                                        selectedTreatment.name
                                            .toLocaleLowerCase()
                                            .replace(/ /g, "-");
                                    router.push(
                                        `/treatments/${formattedTreatmentName}` as Route,
                                    );
                                }}
                                height="h-14"
                                className="w-full lg:w-1/4 uppercase"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Pricing;
